import React, { useState, useEffect } from "react";
import { Save, Eye, FileText } from "lucide-react";
import { toast } from "sonner";

export default function PageEditor() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState("about");
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  useEffect(() => {
    if (selectedPage && pages.length > 0) {
      loadPage(selectedPage);
    }
  }, [selectedPage, pages]);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPages(data);
    } catch (e) {
      toast.error("Failed to load pages");
    } finally {
      setLoading(false);
    }
  };

  const loadPage = (pageKey) => {
    const page = pages.find(p => p.page_key === pageKey);
    if (page) {
      setTitle(page.title);
      setContent(page.content);
    }
  };

  const savePage = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/pages/${selectedPage}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ title, content, published: true })
      });
      toast.success(`${title} page updated!`);
      fetchPages();
    } catch (e) {
      toast.error("Failed to save page");
    } finally {
      setSaving(false);
    }
  };

  const previewPage = () => {
    window.open(`/${selectedPage}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <FileText size={32} className="text-emerald-500" />
              Page Content Editor
            </h1>
            <p className="text-zinc-400 mt-2">Manage About, FAQ, and Updates pages</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={previewPage}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-2 transition"
            >
              <Eye size={18} />
              Preview
            </button>
            <button
              onClick={savePage}
              disabled={saving}
              className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center gap-2 transition disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {/* Page Selector */}
        <div className="flex gap-2 mb-6">
          {["about", "faq", "updates"].map((key) => (
            <button
              key={key}
              onClick={() => setSelectedPage(key)}
              className={`px-6 py-3 rounded-lg font-semibold transition ${
                selectedPage === key
                  ? "bg-emerald-600 text-white"
                  : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
              }`}
            >
              {key.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          {/* Title */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2 text-zinc-300">Page Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
              placeholder="Page Title"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-zinc-300">
              Content (HTML supported)
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={20}
              className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-emerald-500"
              placeholder="<h1>Your heading</h1><p>Your content...</p>"
            />
            <div className="mt-3 text-xs text-zinc-500">
              💡 You can use HTML tags: &lt;h1&gt;, &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;a&gt;, &lt;strong&gt;, &lt;em&gt;
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="mt-8 bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 text-zinc-300">Live Preview</h3>
          <style>{`
            .preview-content { color: white; }
            .preview-content h1 { color: white; font-size: 2.5rem; margin-bottom: 1.5rem; font-weight: bold; }
            .preview-content h2 { color: #f3f4f6; font-size: 2rem; margin-bottom: 1rem; font-weight: 600; }
            .preview-content p { color: #e5e7eb; font-size: 1.125rem; line-height: 1.75; margin-bottom: 1rem; }
            .preview-content a { color: #3b82f6; text-decoration: underline; }
            .preview-content ul { list-style: disc; margin-left: 2rem; color: #e5e7eb; }
            .preview-content li { margin-bottom: 0.5rem; }
          `}</style>
          <div 
            className="preview-content bg-zinc-950 p-8 rounded-lg"
            dangerouslySetInnerHTML={{ __html: content || "<p>Start typing to see preview...</p>" }}
          />
        </div>
      </div>
    </div>
  );
}
